import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import pdfMake from 'pdfmake/build/pdfmake';
import { Ordonnance } from '../../../../models/ordonnance';
import { buildOrdonnanceDocDef, formatDateFr } from './ordonnance-pdf';
import { pdfMakeOrdonnance } from './pdf-setup';

/**
 * Les 4 actions du menu « Partager l'ordonnance », factorisées ici pour être utilisées à la fois par le menu
 * ancré (ordinateur, dans OrdenanceComponent) et par la feuille du bas (téléphone, PartageOrdonnanceSheetComponent) —
 * une seule implémentation, jamais deux à maintenir en parallèle.
 *
 * Contrainte du web, pas de l'application : une page ne peut joindre un fichier à WhatsApp/Messenger/un e-mail
 * que via le partage natif du téléphone (l'utilisateur choisit alors l'appli lui-même dans la feuille proposée
 * par le téléphone) — un simple lien ne le permet jamais. Sur ordinateur, où ce partage natif de fichier
 * n'existe pas, on télécharge le PDF et on ouvre l'appli avec un texte pré-rempli ; l'utilisateur joint le
 * fichier téléchargé lui-même.
 */
@Injectable({ providedIn: 'root' })
export class OrdonnancePartageService {
	constructor(private snackBar: MatSnackBar) {}

	nomFichier(o: Ordonnance): string {
		return `Ordonnance ${o.ferme} ${o.dateOrdonnance} ${o.numCage}.pdf`;
	}

	private texteDePartage(o: Ordonnance): string {
		return `Ordonnance ${o.ferme} — ${formatDateFr(o.dateOrdonnance)} — cage ${o.numCage}`;
	}

	private partageFichierPossible(fichier: File): boolean {
		const nav: any = navigator;
		return !!(nav.canShare && nav.canShare({ files: [fichier] }));
	}

	/** Attend le chargement de la police arabe (une seule fois, voir pdf-setup.ts) avant de générer le PDF. */
	private avecPdf(o: Ordonnance, avecCachet: boolean, avecEntite: boolean, suite: (fichier: File) => void) {
		pdfMakeOrdonnance().then(() => {
			pdfMake.createPdf(buildOrdonnanceDocDef(o, avecCachet, avecEntite)).getBlob((blob: Blob) => {
				suite(new File([blob], this.nomFichier(o), { type: 'application/pdf' }));
			});
		});
	}

	/** Partage natif du téléphone (fichier joint) ; l'utilisateur choisit l'application dans la feuille proposée. */
	private partageNatif(fichier: File, texte: string): boolean {
		const nav: any = navigator;
		if (!this.partageFichierPossible(fichier)) {
			return false;
		}
		nav.share({ files: [fichier], title: texte, text: texte }).catch(() => { /* partage annulé par l'utilisateur */ });
		return true;
	}

	partagerWhatsApp(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		this.avecPdf(ordonnance, avecCachet, avecEntite, (fichier) => {
			const texte = this.texteDePartage(ordonnance);
			if (this.partageNatif(fichier, texte)) {
				return;
			}
			// pdfMake est déjà configuré : avecPdf() a attendu pdfMakeOrdonnance() avant d'appeler ce callback.
			pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).download(this.nomFichier(ordonnance));
			window.open(`https://wa.me/?text=${encodeURIComponent(texte)}`, '_blank');
			this.snackBar.open('PDF téléchargé : joignez-le dans la conversation WhatsApp', 'OK', { duration: 7000 });
		});
	}

	partagerMessengerEmail(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		this.avecPdf(ordonnance, avecCachet, avecEntite, (fichier) => {
			const texte = this.texteDePartage(ordonnance);
			if (this.partageNatif(fichier, texte)) {
				return;
			}
			pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).download(this.nomFichier(ordonnance));
			window.open(`mailto:?subject=${encodeURIComponent(texte)}&body=${encodeURIComponent(texte)}`, '_blank');
			this.snackBar.open('PDF téléchargé : joignez-le à votre message', 'OK', { duration: 7000 });
		});
	}

	/** Feuille de partage du téléphone, sans appli présélectionnée ; sur ordinateur, simple téléchargement. */
	partagerAutresApplications(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		this.avecPdf(ordonnance, avecCachet, avecEntite, (fichier) => {
			const texte = this.texteDePartage(ordonnance);
			if (this.partageNatif(fichier, texte)) {
				return;
			}
			pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).download(this.nomFichier(ordonnance));
			this.snackBar.open('PDF téléchargé', 'OK', { duration: 4000 });
		});
	}

	partagerImprimerPdf(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		pdfMakeOrdonnance().then(() => {
			pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).open();
		});
	}
}
